/**
 * Helper function to prevent submitting empty strings to the API, if a value is actually undefined.
 * Walks through the new data and replaces empty string values with undefined, if the value of the property in the original data was undefined.
 *
 * Why we need this:
 * Getting an empty string for undefined or null values happens when feeding data through a form, because HTML inputs return an empty string for undefined or null values thus changing the data.
 */
export const replaceEmptyStringsWithOriginalUndefined = (
  newData: Record<string, any>,
  originalData: Record<string, any>,
): Record<string, any> => {
  // Handle arrays
  if (Array.isArray(newData)) {
    return newData.map((item, index) => {
      if (typeof item === 'object' && item !== null) {
        return replaceEmptyStringsWithOriginalUndefined(
          item,
          (originalData?.[index] as Record<string, any>) || {},
        );
      }

      return item;
    });
  }

  const result = { ...newData };

  for (const [key, value] of Object.entries(newData)) {
    const originalValue = originalData?.[key];

    if (Array.isArray(value)) {
      result[key] = replaceEmptyStringsWithOriginalUndefined(
        value,
        originalValue || [],
      );
    } else if (typeof value === 'object' && value !== null) {
      result[key] = replaceEmptyStringsWithOriginalUndefined(
        value,
        originalValue || {},
      );
    } else if (value === '' && originalValue === undefined) {
      result[key] = undefined;
    }
  }

  return result;
};
